/**
 * ServiceJsonLd — Injects Schema.org Service + Offer JSON-LD into <Head>
 * for AI crawler discoverability.
 *
 * Supports:
 * - Single-price offers (Offer)
 * - Multi-variant pricing ranges (AggregateOffer)
 * - BreadcrumbList for navigation
 * - Provider information
 */
import Head from 'next/head';
import React from 'react';

interface ServiceJsonLdProps {
  name: string;
  description: string;
  url: string;
  priceCents?: number;
  priceCurrency?: string;
  pricingModel?: string;
  category?: string;
  ratingAvg?: number;
  ratingCount?: number;
  providerName?: string;
  /** For multi-variant: lowest price in cents */
  lowPriceCents?: number;
  /** For multi-variant: highest price in cents */
  highPriceCents?: number;
  /** Number of variant offers */
  offerCount?: number;
  /** Brand/supplier name (distinct from platform provider) */
  brandName?: string;
}

export default function ServiceJsonLd({
  name,
  description,
  url,
  priceCents,
  priceCurrency = 'USD',
  pricingModel,
  category,
  ratingAvg,
  ratingCount,
  providerName = 'Aithon',
  lowPriceCents,
  highPriceCents,
  offerCount,
  brandName,
}: ServiceJsonLdProps) {
  const singlePriceCents =
    typeof priceCents === 'number' && Number.isFinite(priceCents) && priceCents > 0
      ? priceCents
      : undefined;

  const validLowPriceCents =
    typeof lowPriceCents === 'number' && Number.isFinite(lowPriceCents) && lowPriceCents > 0
      ? lowPriceCents
      : undefined;

  const validHighPriceCents =
    typeof highPriceCents === 'number' && Number.isFinite(highPriceCents) && highPriceCents > 0
      ? highPriceCents
      : undefined;

  const hasValidRange =
    validLowPriceCents != null &&
    validHighPriceCents != null &&
    validHighPriceCents >= validLowPriceCents &&
    validLowPriceCents !== validHighPriceCents;

  let offers: Record<string, any> | undefined;

  if (hasValidRange) {
    offers = {
      '@type': 'AggregateOffer',
      lowPrice: (validLowPriceCents! / 100).toFixed(2),
      highPrice: (validHighPriceCents! / 100).toFixed(2),
      priceCurrency,
      offerCount: offerCount || 1,
      availability: 'https://schema.org/InStock',
    };
  } else if (singlePriceCents != null) {
    const priceDollars = (singlePriceCents / 100).toFixed(2);

    offers = {
      '@type': 'Offer',
      price: priceDollars,
      priceCurrency,
      availability: 'https://schema.org/InStock',
    };

    if (pricingModel) {
      offers.priceSpecification = {
        '@type': 'UnitPriceSpecification',
        price: priceDollars,
        priceCurrency,
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          value: 1,
          unitCode: pricingModel === 'monthly' ? 'MON' : 'EA',
        },
      };
    }
  }

  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url,
    provider: {
      '@type': 'Organization',
      name: providerName,
      url: 'https://aithon.tech',
    },
  };

  if (offers) {
    jsonLd.offers = offers;
  }

  // Add brand if distinct from platform
  if (brandName && brandName !== providerName) {
    jsonLd.brand = {
      '@type': 'Brand',
      name: brandName,
    };
  }

  if (category) {
    jsonLd.category = category;
  }

  if (ratingAvg && ratingCount && ratingCount > 0) {
    jsonLd.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingAvg.toFixed(1),
      ratingCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  // Breadcrumb schema
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Marketplace',
        item: 'https://aithon.tech/',
      },
      ...(category
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: category.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              item: `https://aithon.tech/catalog?category=${category}`,
            },
          ]
        : []),
      {
        '@type': 'ListItem',
        position: category ? 3 : 2,
        name,
        item: url,
      },
    ],
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }}
      />
    </Head>
  );
}
